import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux"; // Import the dispatch hook
import { updateClientData, updateStatus, fetchToken, fetchVideoToken } from "../../../redux/clientDataSlice"; // Import the updateClientData action and thunks
import "./clientLoginPopup.css";
import NarrowInputField from "../../../components/input/new/narrowInputField";
import QuitIcon from "./assets/quitIcon.png";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import NarrowPasswordField from "../../../components/input/new/narrowPasswordField";
import { supabase } from "../../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from "../../../network/api";

const ClientLoginPopup = ({ show, onClose }) => {
  const dispatch = useDispatch(); // Initialize the dispatch function
  const [email, setEmail] = useState(""); // Email state
  const [password, setPassword] = useState(""); // Password state
  const [isEditingEmail, setIsEditingEmail] = useState(false); // Toggle for editing email
  const [isEmailEntered, setIsEmailEntered] = useState(false); // Track if email is entered
  const [error, setError] = useState(""); // To show any error messages
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleSubmitEmail = (e) => {
    e.preventDefault();
    if (email.trim() === "") {
      return;
    }
    setIsEmailEntered(true); // Mark email as entered
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();

    // Ensure email and password are provided
    if (!email.trim() || !password.trim()) {
      return;
    }

    try {
      // Step 1: Authenticate the user with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message || "Authentication failed");
        console.error("Authentication failed:", error.message);
        return;
      }

      // Step 2: Send the access token (JWT) to the backend to verify the account type
      const accessToken = data.session.access_token;

      const response = await authenticatedFetch("/api/client/clientSignIn", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`, // Include the access token
        },
        body: JSON.stringify({ email }),
      });

      const responseData = await response.json();

      if (response.ok) {
        console.log("Login and verification successful:", responseData);

        // Manually update the client data in Redux
        // Assuming the data returned from API contains the client data
        dispatch(updateClientData(responseData.clientDetails)); // Here you would send the client data
        dispatch(updateStatus("online")); // Set the status to "online"

        // Fetch Stream chat and video tokens
        if (responseData.clientDetails && responseData.clientDetails.user_id) {
          dispatch(fetchToken(responseData.clientDetails.user_id));
          dispatch(fetchVideoToken(responseData.clientDetails.user_id));
        }

        onClose(); // Close the popup after successful login
      } else {
        setError(responseData.message || "Failed to verify account type");
        console.error("Verification failed:", responseData.message);
        await supabase.auth.signOut();
        return;
      }
    } catch (error) {
      setError("An error occurred during login.");
      console.error("Error during sign-in:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onClose]);

  const handleForgotPassword = () => {
    navigate("/auth/reset-password");
  };

  if (!show) return null; // Don't render anything if show is false

  return (
    <div className="login-popup-overlay">
      <div className="login-popup" ref={menuRef}>
        <div className="login-popup-header">
          <h3 className="login-popup-header-title">
            {isEmailEntered
              ? "Enter your password"
              : "To log in, enter your email address"}
          </h3>
          <img
            src={QuitIcon}
            onClick={onClose}
            style={{ width: "24px", height: "24px", cursor: "pointer" }}
            alt="quit menu"
          />
        </div>

        <div className="input-group">
          {!isEmailEntered ? (
            // Email input field
            <NarrowInputField
              name="Email Address"
              title="Email Address"
              placeholder="Enter your email"
              isRequired={true}
              value={email}
              onChange={handleEmailChange}
              halfWidth={false}
            />
          ) : (
            <>
              {!isEditingEmail ? (
                <div className="email-display">
                  <span className="login-email-text">{email}</span>
                  <button
                    className="edit-email-btn"
                    onClick={() => setIsEditingEmail(true)}
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <NarrowInputField
                  name="Email Address"
                  title="Email Address"
                  placeholder="Enter your email"
                  isRequired={true}
                  value={email}
                  onChange={handleEmailChange}
                  halfWidth={false}
                />
              )}

              <NarrowPasswordField
                name="Password"
                title="Password"
                placeholder="Enter your password"
                isRequired={true}
                value={password}
                onChange={handlePasswordChange}
                halfWidth={false}
                type="password"
              />
              <p onClick={handleForgotPassword} className="forgot-password">
                Forgot your password?
              </p>
            </>
          )}
        </div>

        {/* Display any error message */}
        {error && <div className="error-message">{error}</div>}

        <button
          className="popup-continue-btn"
          onClick={isEmailEntered ? handleSubmitPassword : handleSubmitEmail}
          disabled={!email.trim()} // Disable continue until email is entered
        >
          Continue
        </button>

        <div className="divider">
          <span>or</span>
        </div>
        <hr className="h-rule-popup" />

        <div className="social-login-buttons">
          <button className="google-btn">
            <FcGoogle style={{ paddingBottom: "1px" }} />
            Continue with Google
          </button>
          <button className="apple-btn">
            <FaApple style={{ paddingBottom: "3px" }} />
            Continue with Apple
          </button>
        </div>

        <div className="create-account">
          <span className="popup-login-cta">
            New to quidpro?{" "}
            <a className="popup-login-cta-href" href="/createuser/auth">
              Create an account
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClientLoginPopup;
